import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { serverDomain } from "../../../config";
import moment from "moment-timezone";
import WalletPopup from "../../../components/WalletPopup";
import "../../../styles/PayoutsPage.css";

const methodLabel = (m) => {
  switch (m) {
    case "USDT_TRC20": return "USDT TRC20";
    case "USDT_BEP20": return "USDT BEP20";
    case "CRYPTO_BOT_CHECK": return "Crypto Bot Чек";
    case "IP_OOO": return "ИП/ООО";
    case "SELF_EMPLOYED": return "Самозанятый";
    default: return "—";
  }
};

const statusLabel = (s) => {
  switch (s) {
    case "pending": return "Ожидает";
    case "paid": return "Выплачено";
    case "approved": return "Выплачено"
    case "rejected": return "Отклонено";
    default: return s || "—";
  }
};

const statusClass = (s) => {
  if (s === "pending") return "fl-status fl-status--pending";
  if (s === "paid" || s === "approved") return "fl-status fl-status--approved";
  if (s === "rejected") return "fl-status fl-status--rejected";
  return "fl-status";
};

const PayoutsPage = () => {
  const navigate = useNavigate();

  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // comment edit
  const [editingId, setEditingId] = useState(null);
  const [draftComment, setDraftComment] = useState("");
  const [savingId, setSavingId] = useState(null);
  const textareaRef = useRef(null);

  // fee
  const [fee, setFee] = useState("");
  const [selectedFeePayoutId, setSelectedFeePayoutId] = useState(null);

  // wallet popup
  const [walletDetails, setWalletDetails] = useState(null);
  const [openedWalletId, setOpenedWalletId] = useState(null);
  const [walletPopupStyle, setWalletPopupStyle] = useState({});

  // ui
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const formatDate = (isoDate) => moment.utc(isoDate).local().format("DD.MM.YYYY HH:mm:ss");

  const fetchPayouts = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const res = await fetch(`${serverDomain}/api/admin/payouts`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data?.error || "Ошибка загрузки выплат");

      setPayouts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError(e.message || "Ошибка");
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (payouts || []).filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (!s) return true;

      const hay = [
        p.id,
        p.email,
        p.telegram_username,
        p.wallet_info,
        p.payment_method,
        p.status,
        p.comment,
        p.original_amount,
        p.fee,
      ]
        .map((x) => (x == null ? "" : String(x).toLowerCase()))
        .join(" ");

      return hay.includes(s);
    });
  }, [payouts, q, statusFilter]);

  const counts = useMemo(() => {
    const all = filtered.length;
    const pending = filtered.filter((p) => p.status === "pending").length;
    return { all, pending };
  }, [filtered]);

  const handleWalletClick = async (e, walletId, payoutId) => {
    const token = localStorage.getItem("token");
    const target = e.currentTarget;

    if (openedWalletId === payoutId) {
      setOpenedWalletId(null);
      setWalletDetails(null);
      return;
    }

    try {
      const response = await fetch(`${serverDomain}/api/admin/wallets/${walletId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Ошибка получения информации о кошельке");

      setWalletDetails(data);
      setOpenedWalletId(payoutId);

      const rect = target.getBoundingClientRect();
      const margin = 10;
      const popupHeight = 250;
      const popupWidth = 250;

      let top;
      if (window.innerHeight - rect.bottom >= popupHeight + margin) top = rect.bottom + margin;
      else if (rect.top >= popupHeight + margin) top = rect.top - popupHeight - margin;
      else top = Math.max(margin, window.innerHeight - popupHeight - margin);

      let left = rect.left;
      if (left + popupWidth > window.innerWidth) left = window.innerWidth - popupWidth - margin;

      setWalletPopupStyle({ top: `${top}px`, left: `${left}px` });
    } catch (err) {
      console.error("Ошибка получения кошелька:", err);
      alert("Ошибка загрузки информации о кошельке");
    }
  };

  // comment editor
  const startEdit = (payout) => {
    setEditingId(payout.id);
    setDraftComment(payout.comment || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraftComment("");
  };

  const applyLocalComment = (payoutId, text) => {
    setPayouts((prev) => prev.map((p) => (p.id === payoutId ? { ...p, comment: text } : p)));
  };

  const saveComment = async (payoutId, text) => {
    const token = localStorage.getItem("token");
    setSavingId(payoutId);

    try {
      const res = await fetch(`${serverDomain}/api/admin/payouts/${payoutId}/comment`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ comment: text }),
      });

      if (res.ok) {
        const data = await res.json().catch(() => null);
        applyLocalComment(payoutId, data?.comment ?? text);
      } else {
        applyLocalComment(payoutId, text);
      }
    } catch {
      applyLocalComment(payoutId, text);
    } finally {
      setSavingId(null);
      setEditingId(null);
    }
  };

  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "0px";
    const h = textareaRef.current.scrollHeight;
    textareaRef.current.style.height = Math.min(h, 160) + "px";
  }, [draftComment, editingId]);

  // approve / reject
  const handleApprovePayout = async (payoutId) => {
    const token = localStorage.getItem("token");
    const current = payouts.find((p) => p.id === payoutId);
    const body = {
      comment: current?.comment || "",
      fee: selectedFeePayoutId === payoutId ? fee : "",
    };

    try {
      const res = await fetch(`${serverDomain}/api/admin/payouts/approve/${payoutId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error("Ошибка при подтверждении выплаты");

      setPayouts((prev) => prev.map((p) => (p.id === payoutId ? { ...p, status: "paid" } : p)));

      if (selectedFeePayoutId === payoutId) {
        setFee("");
        setSelectedFeePayoutId(null);
      }
    } catch (e) {
      console.error(e);
      alert("Не удалось подтвердить выплату");
    }
  };

  const handleRejectPayout = async (payoutId) => {
    const token = localStorage.getItem("token");
    const current = payouts.find((p) => p.id === payoutId);
    const body = { comment: current?.comment || "" };

    try {
      const res = await fetch(`${serverDomain}/api/admin/payouts/reject/${payoutId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error("Ошибка при отклонении выплаты");

      setPayouts((prev) => prev.map((p) => (p.id === payoutId ? { ...p, status: "rejected" } : p)));
    } catch (e) {
      console.error(e);
      alert("Не удалось отклонить выплату");
    }
  };

  return (
    <div className="fl-adminpage fl-apayouts">
      <div className="fl-adminpage__head">
        <h2 className="fl-h2">Заявки на выплаты</h2>
        <div className="fl-adminpage__meta">
          Всего: <b>{counts.all}</b> · Ожидают: <b>{counts.pending}</b>
        </div>
      </div>

      <div className="fl-toolbar fl-apayouts__toolbar">
        <input
          className="fl-input fl-input--wide"
          placeholder="Поиск: email / tg / кошелёк / id…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        <select className="fl-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">Все статусы</option>
          <option value="pending">Ожидает</option>
          <option value="paid">Выплачено</option>
          <option value="rejected">Отклонено</option>
        </select>

        <button className="fl-btn fl-btn--ghost" type="button" onClick={fetchPayouts} disabled={loading}>
          {loading ? "Загрузка…" : "Обновить"}
        </button>
      </div>

      {error ? <div className="fl-alert fl-alert--error">{error}</div> : null}

      {loading ? (
        <div className="loading-container">
          <div className="loader" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="fl-empty">Нет заявок на выплату</div>
      ) : (
        <div className="fl-table-wrap">
          <table className="fl-table fl-apayouts__table">
            <thead>
              <tr>
                <th className="col-id">ID</th>
                <th className="col-date">Дата создания</th>
                <th className="col-user">Email / TG</th>
                <th className="col-sum">Сумма</th>
                <th className="col-fee">Комиссия</th>
                <th className="col-wallet">Кошелёк</th>
                <th className="col-method">Метод</th>
                <th className="col-status">Статус</th>
                <th className="col-comment">Комментарий</th>
                <th className="col-actions">Действия</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((payout) => {
                const net = Number(payout.original_amount || 0) - Number(payout.fee || 0);

                return (
                  <tr key={payout.id}>
                    <td className="col-id">{payout.id}</td>
                    <td className="col-date">{formatDate(payout.created_at)}</td>

                    <td className="col-user">
                      <div className="fl-apayouts__email">{payout.email}</div>
                      <div className="fl-muted">{payout.telegram_username || "—"}</div>
                    </td>

                    <td className="col-sum">{net} RUB</td>
                    <td className="col-fee">{payout.fee} RUB</td>

                    <td className="col-wallet fl-apayouts__walletCell">
                      <button
                        type="button"
                        className="fl-linklike"
                        onClick={(e) => handleWalletClick(e, payout.wallet_id, payout.id)}
                        title="Показать реквизиты"
                      >
                        {payout.wallet_info || "—"}
                      </button>
                    </td>

                    <td className="col-method">{methodLabel(payout.payment_method)}</td>

                    <td className="col-status">
                      <span className={statusClass(payout.status)}>{statusLabel(payout.status)}</span>
                    </td>

                    <td className="col-comment">
                      {editingId !== payout.id ? (
                        <div className="fl-apayouts__commentView" onClick={() => startEdit(payout)} title="Нажмите, чтобы редактировать">
                          {payout.comment || <span className="fl-muted">Нет комментария</span>}
                        </div>
                      ) : (
                        <div className="fl-apayouts__commentEdit">
                          <div className="fl-apayouts__commentRow">
                            <textarea
                              ref={textareaRef}
                              className="fl-textarea"
                              value={draftComment}
                              onChange={(e) => setDraftComment(e.target.value)}
                              placeholder="Введите комментарий…"
                              rows={1}
                              maxLength={1000}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                  e.preventDefault();
                                  saveComment(payout.id, draftComment.trim());
                                } else if (e.key === "Escape") {
                                  e.preventDefault();
                                  cancelEdit();
                                }
                              }}
                            />
                            <div className="fl-apayouts__commentBtns">
                              <button
                                className="fl-iconbtn fl-iconbtn--ok"
                                type="button"
                                onClick={() => saveComment(payout.id, draftComment.trim())}
                                disabled={savingId === payout.id}
                                title="Сохранить (Enter)"
                              >
                                {savingId === payout.id ? "…" : "✓"}
                              </button>
                              <button
                                className="fl-iconbtn fl-iconbtn--no"
                                type="button"
                                onClick={cancelEdit}
                                disabled={savingId === payout.id}
                                title="Отменить (Esc)"
                              >
                                ✕
                              </button>
                            </div>
                          </div>

                          <div className="fl-apayouts__hint">Enter — сохранить · Shift+Enter — новая строка · Esc — отменить</div>
                        </div>
                      )}
                    </td>

                    <td className="col-actions">
                      {payout.status === "pending" ? (
                        <div className="fl-apayouts__actions">
                          <div>
                          <input
                            style={{marginBottom: "10px"}}
                            className="fl-input fl-input--mini"
                            type="number"
                            placeholder="Комиссия"
                            value={selectedFeePayoutId === payout.id ? fee : ""}
                            onChange={(e) => {
                              setSelectedFeePayoutId(payout.id);
                              setFee(e.target.value);
                            }}
                          />
                          </div>
                          <div>
                            <button style={{marginBottom: "10px"}} className="fl-btn fl-btn--mini" type="button" onClick={() => handleApprovePayout(payout.id)}>
                                ✅ Выплатить
                            </button>
                          </div>
                          <div>
                            <button style={{marginBottom: "10px"}} className="fl-btn fl-btn--mini fl-btn--danger" type="button" onClick={() => handleRejectPayout(payout.id)}>
                                ❌ Отклонить
                            </button>
                          </div>
                        </div>
                      ) : (
                        <span className="fl-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openedWalletId && walletDetails && (
        <WalletPopup
          style={walletPopupStyle}   // оставляем inline — это позиционирование
          walletDetails={walletDetails}
          onClose={() => setOpenedWalletId(null)}
        />
      )}
    </div>
  );
};

export default PayoutsPage;